'use client';

import { useEffect, useRef, type ComponentProps } from 'react';
import { HorizontalScrollContainer } from '../../HorizontalScrollContainer/HorizontalScrollContainer';
import type { ParticipationState } from '../../lottery-purchases/types';
import { DrawSelectorItem, type DrawSelectorVariant } from '../DrawSelectorItem/DrawSelectorItem';
import { getState } from './utils/getState/getState';

type DrawSelectorItemType = {
  date: Date;
  drawingId: string;
  isSelected: boolean;
  isDisabled: boolean;
  participationState: ParticipationState;
};

type DrawSelectorProps = ComponentProps<'div'> & {
  items: DrawSelectorItemType[];
  onDateSelect: (date: Date) => void;
  variant?: DrawSelectorVariant;
  olderDraws?: boolean;
  olderDrawsBtnSelected?: boolean;
  onOlderDrawsClick?: () => void;
};

/**
 * DrawSelector is a component that displays a list of draw selector items with different visual states.
 * It accepts a list of items and a function to handle the date selection.
 *
 * @param items - The list of draw selector items.
 * @param onDateSelect - The function to handle the date selection.
 * @param variant - Variant style for the buttons.
 * @param olderDraws - Flag to show older bets button in Draw Selector.
 * @param olderDrawsBtnSelected - Flag to show older draws button as selected.
 * @param onOlderDrawsClick - The function to handle the olderDraws button click.
 *
 * @returns The DrawSelector component.
 */
function DrawSelector({ items, onDateSelect, variant, olderDraws, olderDrawsBtnSelected = false, onOlderDrawsClick = () => {}, ...props }: DrawSelectorProps) {
  const selectedItemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, []);

  let itemsContent = items.map(({ date, drawingId, isSelected, isDisabled, participationState }) => (
    <DrawSelectorItem
      key={date.toISOString()}
      ref={isSelected ? selectedItemRef : undefined}
      date={date}
      drawingId={drawingId}
      state={getState({ isSelected, isDisabled })}
      variant={variant}
      onClick={() => onDateSelect(date)}
      role="option"
      participationState={participationState}
      data-track-name="Date"
    />
  ));

  if (olderDraws) {
    const olderDrawsBtn = (
      <DrawSelectorItem
        olderDraws={true}
        key="olderDraws"
        state={getState({ isSelected: olderDrawsBtnSelected, isDisabled: false })}
        variant="olderDraws"
        onClick={() => onOlderDrawsClick()}
        role="option"
        data-testid="olderDrawsBtn"
      />
    );

    itemsContent = [olderDrawsBtn, ...itemsContent];
  }

  return (
    <HorizontalScrollContainer role="listbox" {...props}>
      {itemsContent}
    </HorizontalScrollContainer>
  );
}

export { DrawSelector };
export type { DrawSelectorItemType };
